// Home/dashboard page
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import scrapeJobApi from '../services/scrapeJobApi';
import analysisJobApi from '../services/analysisJobApi';
import fileSetApi from '../services/fileSetApi';
import Loading from '../components/Loading';

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [recentScrapeJobs, setRecentScrapeJobs] = useState([]);
  const [recentAnalysisJobs, setRecentAnalysisJobs] = useState([]);
  const [recentFileSets, setRecentFileSets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch recent data (limit to 5 items each)
        const [scrapeJobs, analysisJobs, fileSets] = await Promise.all([
          scrapeJobApi.getScrapeJobs({ all: true }),
          analysisJobApi.getAnalysisJobs({ all: true }),
          fileSetApi.getFileSets(),
        ]);
        
        // Sort by creation date and take the 5 most recent
        setRecentScrapeJobs(
          scrapeJobs
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
        );
        
        setRecentAnalysisJobs(
          analysisJobs
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
        );
        
        setRecentFileSets(
          fileSets
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 5)
        );
      } catch (err) {
        setError(err.message);
        console.error('Error fetching dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Job status badge
  const StatusBadge = ({ status }) => {
    let color;
    switch (status) {
      case 'completed':
        color = 'bg-green-100 text-green-800';
        break;
      case 'failed':
        color = 'bg-red-100 text-red-800';
        break;
      case 'pending':
        color = 'bg-yellow-100 text-yellow-800';
        break;
      default:
        color = 'bg-blue-100 text-blue-800';
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return <Loading message="Loading dashboard data..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">Error loading dashboard: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">DMT Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
        {/* Welcome to DMT.  */}
        Welcome to the Digital Marketing Transformation tool. 
          Create web scraping jobs to download website content, 
          manage custom uploaded files, and run AI analysis on the data.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Link
          to="/advanced/scrape-jobs/new"
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow p-4 text-center transition-colors"
        >
          <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
          </svg>
          <h3 className="text-lg font-medium">New Web Scraping Job</h3>
          <p className="text-sm mt-1">Crawl a website and download html and screenshots</p>
        </Link>

        <Link
          to="/advanced/file-sets"
          className="bg-green-500 hover:bg-green-600 text-white rounded-lg shadow p-4 text-center transition-colors"
        >
          <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
          </svg>
          <h3 className="text-lg font-medium">Manage Custom Files</h3>
          <p className="text-sm mt-1">Upload and organize files, for later inclusion in analyses</p>
        </Link>

        <Link
          to="/advanced/analysis-jobs/new"
          className="bg-purple-500 hover:bg-purple-600 text-white rounded-lg shadow p-4 text-center transition-colors"
        >
          <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
          </svg>
          <h3 className="text-lg font-medium">New Analysis</h3>
          <p className="text-sm mt-1">Analyze content with AI and provide recommendations</p>
        </Link>
        
        <Link
          to="/"
          className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg shadow p-4 text-center transition-colors"
        >
          <svg className="h-8 w-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
          </svg>
          <h3 className="text-lg font-medium">Simple Mode</h3>
          <p className="text-sm mt-1">Return to simplified site analysis interface</p>
        </Link>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Scrape Jobs */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Scrape Jobs</h2>
            <Link to="/advanced/scrape-jobs" className="text-sm text-blue-600 hover:text-blue-800">
              View All
            </Link>
          </div>
          
          {recentScrapeJobs.length === 0 ? (
            <p className="text-gray-500 text-sm">No scrape jobs found.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentScrapeJobs.map((job) => (
                <li key={job.jobId} className="py-3">
                  <Link to={`/advanced/scrape-jobs/${job.jobId}`} className="block hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div className="truncate">
                        <p className="text-sm font-medium text-gray-900 truncate" title={job.targetUrl}>
                          {job.targetUrl}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {formatDate(job.createdAt)}
                        </p>
                      </div>
                      <StatusBadge status={job.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>



        {/* Recent File Sets */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent File Sets</h2>
            <Link to="/advanced/file-sets" className="text-sm text-blue-600 hover:text-blue-800">
              View All
            </Link>
          </div>
          
          {recentFileSets.length === 0 ? (
            <p className="text-gray-500 text-sm">No file sets found.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentFileSets.map((fileSet) => (
                <li key={fileSet.fileSetId} className="py-3">
                  <Link to={`/advanced/file-sets/${fileSet.fileSetId}`} className="block hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {fileSet.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {formatDate(fileSet.createdAt)}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {fileSet.fileCount || 0} files
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>


        {/* Recent Analysis Jobs */}
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Recent Analysis Jobs</h2>
            <Link to="/advanced/analysis-jobs" className="text-sm text-blue-600 hover:text-blue-800">
              View All
            </Link>
          </div>
          
          {recentAnalysisJobs.length === 0 ? (
            <p className="text-gray-500 text-sm">No analysis jobs found.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentAnalysisJobs.map((job) => (
                <li key={job.jobId} className="py-3">
                  <Link to={`/advanced/analysis-jobs/${job.jobId}`} className="block hover:bg-gray-50">
                    <div className="flex justify-between items-center">
                      <div className="truncate">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {(job.prompt && job.prompt.substring(0, 40) + '...') || 'Analysis Job'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {formatDate(job.createdAt)}
                        </p>
                      </div>
                      <StatusBadge status={job.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>


      </div>
    </div>
  );
};

export default Home;